import { API, graphqlOperation } from 'aws-amplify';
import { recordAnalyticsEvent } from './Analytics/recordAnalyticsEvent';

import {
  listTemplates,
  getTemplate,
  listDocuments,
  listNotifications,
  listUsers,
  getUser,
  listBundles,
  listDocumentsByEmail,
  listBundlesWithDocuments,
  listExportHistories
} from './graphql/queries';
import {
  listTemplatesDashboard,
  getBundleWithDocs,
  customGetDocument
} from './graphql/customQueries';
import {
  createDocument,
  updateTemplate,
  createTemplate,
  updateUser,
  updateBundle,
  updateNotification
} from './graphql/mutations';
import { updateDocumentCustom } from './graphql/customMutations';
import { maxItemPerBatch } from './customHooks/useInfiniteScroll';

const processError = (error) => {
  if (error === 'No current user') {
    window.location.reload();
  }
  throw error;
};

const GraphqlApi = {
  allTemplates: function () {
    return this.templates;
  },
  getTemplates: function (id) {
    const isTemplate = (p) => p.number === id;
    return this.templates.find(isTemplate);
  },
  getTemplate: async function (id) {
    try {
      const data = await API.graphql(graphqlOperation(getTemplate, { id: id }));
      return data;
    } catch (error) {
      processError(error);
    }
  },

  updateTemplate: async function (updateTemplateInput, subgroup) {
    try {
      updateTemplateInput.subgroups = [subgroup];

      delete updateTemplateInput.document;

      const data = await API.graphql(
        graphqlOperation(updateTemplate, { input: updateTemplateInput })
      );
      const attributes = {
        templateId: updateTemplateInput.id,
        templateName: updateTemplateInput.name,
        owner: updateTemplateInput.owner
      };
      if (updateTemplateInput.status !== 'DELETED') {
        recordAnalyticsEvent('template.updated', attributes);
      } else {
        recordAnalyticsEvent('template.deleted', attributes);
      }

      return data;
    } catch (error) {
      processError(error);
    }
  },

  listTemplates: async function (filter, nextToken) {
    try {
      const { createdAt, ..._filter } = filter;
      const _createdAt = createdAt ? createdAt.ge : undefined;
      const data = await API.graphql(
        graphqlOperation(listTemplates, {
          filter: _filter,
          createdAt: _createdAt,
          nextToken: nextToken
        })
      );
      return data;
    } catch (error) {
      processError(error);
    }
  },
  listTemplatesDashboard: async function (filter, subgroup, nextToken) {
    try {
      const _filter = {
        subgroups: { contains: subgroup },
        ...filter
      };
      const data = await API.graphql(
        graphqlOperation(listTemplatesDashboard, {
          filter: _filter,
          nextToken: nextToken
        })
      );
      return data;
    } catch (error) {
      processError(error);
    }
  },
  listDocuments: async function (filter, nextToken, abortSignal) {
    try {
      let enougthResults = false;
      let documents;
      while (!enougthResults) {
        if (abortSignal && abortSignal.aborted) {
          // eslint-disable-next-line no-undef
          throw new DOMException('Aborted', 'AbortError');
        }
        const temporalDocuments = await API.graphql(
          graphqlOperation(listDocuments, {
            filter,
            nextToken
          })
        );
        if (!documents) {
          documents = temporalDocuments;
        } else {
          if (temporalDocuments.data.listDocuments.items && temporalDocuments.data.listDocuments.items.length > 0) {
            documents.data.listDocuments.items = [...documents.data.listDocuments.items, ...temporalDocuments.data.listDocuments.items];
          }
          documents.data.listDocuments.nextToken = temporalDocuments.data.listDocuments.nextToken;
        }

        nextToken = documents.data.listDocuments.nextToken;

        if (!documents || !documents.data || !documents.data.listDocuments || !nextToken || documents.data.listDocuments.items.length > maxItemPerBatch) {
          enougthResults = true;
        }
      }

      return [documents, nextToken];
    } catch (error) {
      if (error.name === 'AbortError') {
        return [null, null];
      }
      processError(error);
    }
  },
  listDocumentsByEmail: async function (filter, nextToken) {
    try {
      const documents = await API.graphql(
        graphqlOperation(listDocumentsByEmail, { filter, nextToken })
      );
      nextToken = documents.data.listDocumentsByEmail.nextToken;
      return [documents, nextToken];
    } catch (error) {
      console.log(error);
    }
  },

  listBundles: async function (filter, nextToken, searchByDocument, abortSignal) {
    try {
      let enougthResults = false;
      let bundles;

      while (!enougthResults) {
        if (abortSignal && abortSignal.aborted) {
          // eslint-disable-next-line no-undef
          throw new DOMException('Aborted', 'AbortError');
        }

        const { filterDoc, ..._filter } = filter;
        const temporalBundles = await API.graphql(
          graphqlOperation(searchByDocument ? listBundlesWithDocuments : listBundles, {
            filter: _filter,
            filterDoc,
            nextToken
          })
        );

        if (!bundles) {
          bundles = temporalBundles;
        } else {
          if (temporalBundles.data.listBundles.items && temporalBundles.data.listBundles.items.length > 0) {
            bundles.data.listBundles.items = [...bundles.data.listBundles.items, ...temporalBundles.data.listBundles.items];
          }
          bundles.data.listBundles.nextToken = temporalBundles.data.listBundles.nextToken;
        }

        nextToken = bundles.data.listBundles.nextToken;

        if (!bundles || !bundles.data || !bundles.data.listBundles || !nextToken || bundles.data.listBundles.items.length > maxItemPerBatch) {
          enougthResults = true;
        }
      }

      return [bundles, nextToken];
    } catch (error) {
      processError(error);
    }
  },
  getBundle: async function (id, nextToken) {
    try {
      const data = await API.graphql(
        graphqlOperation(getBundleWithDocs, { id: id, nextToken: nextToken })
      );
      return data;
    } catch (error) {
      processError(error);
    }
  },
  getDocument: async function (id) {
    try {
      const data = await API.graphql(graphqlOperation(customGetDocument, { id: id }));
      return data;
    } catch (error) {
      processError(error);
    }
  },
  createDocument: async function (createDocumentInput, subgroup) {
    try {
      createDocumentInput.subgroups = [subgroup];
      const data = await API.graphql(
        graphqlOperation(createDocument, { input: createDocumentInput })
      );
      if (createDocumentInput.type === 'file') {
        recordAnalyticsEvent('hash.created', {
          documentId: createDocumentInput.id,
          documentName: createDocumentInput.name
        });
      } else if (createDocumentInput.type === 'document') {
        recordAnalyticsEvent('document.created', {
          documentId: createDocumentInput.id,
          documentName: createDocumentInput.name
        });
      }

      return data;
    } catch (error) {
      processError(error);
    }
  },
  updateDocument: async function (updateDocumentInput) {
    try {
      delete updateDocumentInput.template;
      delete updateDocumentInput.bundle;
      delete updateDocumentInput.pdfUrl;
      delete updateDocumentInput.flow;
      delete updateDocumentInput.user;
      delete updateDocumentInput.users;
      const attributes = {
        documentId: updateDocumentInput.id,
        documentName: updateDocumentInput.name
      };

      const data = await API.graphql(
        graphqlOperation(updateDocumentCustom, { input: updateDocumentInput })
      );
      if (updateDocumentInput.type === 'file') {
        if (updateDocumentInput.status !== 'deleted') {
          recordAnalyticsEvent('hash.updated', attributes);
        } else {
          recordAnalyticsEvent('hash.deleted', attributes);
        }
      } else if (updateDocumentInput.type === 'document') {
        if (updateDocumentInput.status !== 'deleted') {
          recordAnalyticsEvent('document.updated', attributes);
        } else {
          recordAnalyticsEvent('document.deleted', attributes);
        }
      }
      return data;
    } catch (error) {
      processError(error);
    }
  },
  updateBundle: async function (updateBundleInput, subgroup) {
    try {
      if (subgroup) {
        updateBundleInput.subgroups = [subgroup];
      }
      delete updateBundleInput.template;
      delete updateBundleInput.flowTemplate;
      delete updateBundleInput.document;
      delete updateBundleInput.bundle;
      delete updateBundleInput.pdfUrl;
      delete updateBundleInput.flow;
      delete updateBundleInput.templateName;

      const data = await API.graphql(
        graphqlOperation(updateBundle, { input: updateBundleInput })
      );
      if (updateBundleInput.status !== 'deleted') {
        recordAnalyticsEvent('bundle.updated', {
          bundleId: updateBundleInput.id,
          bundleName: updateBundleInput.name,
          owner: updateBundleInput.owner
        });
      } else {
        recordAnalyticsEvent('bundle.deleted', {
          bundleId: updateBundleInput.id,
          bundleName: updateBundleInput.name,
          owner: updateBundleInput.owner
        });
      }
      return data;
    } catch (error) {
      processError(error);
    }
  },
  listUsers: async function (filter, subgroup, nextToken) {
    try {
      const _filter = {
        subgroups: { contains: subgroup },
        ...filter
      };
      const users = await API.graphql(
        graphqlOperation(listUsers, {
          filter: _filter,
          nextToken: nextToken
        })
      );
      return users;
    } catch (error) {
      processError(error);
    }
  },
  listUsersForBioadmin: async function () {
    try {
      const users = await API.graphql(
        graphqlOperation(listUsers)
      );
      return users;
    } catch (error) {
      processError(error);
    }
  },
  getUser: async function (id) {
    try {
      const user = await API.graphql(
        graphqlOperation(getUser, { userId: id })
      );
      return user;
    } catch (error) {
      processError(error);
    }
  },
  updateUser: async function (UpdateUserInput, subgroup) {
    try {
      UpdateUserInput.subgroups = subgroup;

      const user = await API.graphql(
        graphqlOperation(updateUser, { input: UpdateUserInput })
      );
      return user;
    } catch (error) {
      processError(error);
    }
  },
  createTemplate: async function (createTemplateInput, subgroup) {
    try {
      createTemplateInput.subgroups = [subgroup];

      const data = await API.graphql(
        graphqlOperation(createTemplate, { input: createTemplateInput })
      );
      recordAnalyticsEvent('template.created', {
        templateId: data.data.createTemplate.id,
        templateName: data.data.createTemplate.name,
        owner: data.data.createTemplate.owner
      });
      return data;
    } catch (error) {
      processError(error);
    }
  },
  listNotifications: async function (filter, subgroup, nextToken) {
    try {
      const _filter = {
        subgroups: { contains: subgroup },
        ...filter
      };
      const notifications = await API.graphql(
        graphqlOperation(listNotifications, {
          filter: _filter,
          nextToken: nextToken
        })
      );
      return notifications;
    } catch (error) {
      processError(error);
    }
  },
  listExportHistories: async function (id, filter, nextToken) {
    try {
      const exportHistories = await API.graphql(
        graphqlOperation(listExportHistories, {
          id: id,
          filter: filter,
          nextToken: nextToken
        })
      );
      return exportHistories;
    } catch (error) {
      processError(error);
    }
  },
  updateNotifications: async function (updateNotificationInput) {
    try {
      const data = await API.graphql(
        graphqlOperation(updateNotification, { input: updateNotificationInput })
      );
      return data;
    } catch (error) {
      processError(error);
    }
  }
};

export default GraphqlApi;
